import { PASSWORD_CONSTANTS, PASSWORD_FEEDBACK } from '../constants/password';
import { UPPERCASE_CHARS, LOWERCASE_CHARS, NUMBER_CHARS } from '../constants/validation';

export interface PasswordStrength {
  isValid: boolean;
  score: number;
  level: 'Weak' | 'Fair' | 'Good' | 'Strong';
  color: string;
  feedback: string[];
}

export const PASSWORD_REQUIREMENTS = {
  MIN_LENGTH: PASSWORD_CONSTANTS.MIN_LENGTH,
  MAX_LENGTH: PASSWORD_CONSTANTS.MAX_LENGTH,
  MIN_SCORE: PASSWORD_CONSTANTS.MIN_SCORE,
  SCORE_WEIGHTS: PASSWORD_CONSTANTS.SCORE_WEIGHTS
} as const;

/**
 * Validates password strength according to enterprise security requirements
 * @param password - The password to validate
 * @returns PasswordStrength object with validation results
 */
export const validatePassword = (password: string): PasswordStrength => {
  let score = 0;
  const feedback: string[] = [];

  if (!password) {
    return {
      isValid: false,
      score: 0,
      level: 'Weak',
      color: 'bg-gray-300',
      feedback
    };
  }

  const hasMinLength = password.length >= PASSWORD_REQUIREMENTS.MIN_LENGTH;
  const hasUppercase = UPPERCASE_CHARS.test(password);
  const hasLowercase = LOWERCASE_CHARS.test(password);
  const hasNumber = NUMBER_CHARS.test(password);

  if (hasMinLength) {
    score += PASSWORD_CONSTANTS.SCORE_WEIGHTS.LENGTH_BASE;
  } else {
    feedback.push(PASSWORD_FEEDBACK.MIN_LENGTH);
  }

  if (password.length > PASSWORD_REQUIREMENTS.MAX_LENGTH) {
    feedback.push(PASSWORD_FEEDBACK.MAX_LENGTH);
  }

  if (password.length >= 12) {
    score += PASSWORD_CONSTANTS.SCORE_WEIGHTS.LENGTH_BONUS_12;
  }

  if (password.length >= 16) {
    score += PASSWORD_CONSTANTS.SCORE_WEIGHTS.LENGTH_BONUS_16;
  }

  if (hasUppercase) {
    score += PASSWORD_CONSTANTS.SCORE_WEIGHTS.UPPERCASE;
  } else {
    feedback.push(PASSWORD_FEEDBACK.UPPERCASE);
  }

  if (hasLowercase) {
    score += PASSWORD_CONSTANTS.SCORE_WEIGHTS.LOWERCASE;
  } else {
    feedback.push(PASSWORD_FEEDBACK.LOWERCASE);
  }

  if (hasNumber) {
    score += PASSWORD_CONSTANTS.SCORE_WEIGHTS.NUMBER;
  } else {
    feedback.push(PASSWORD_FEEDBACK.NUMBER);
  }

  score = Math.max(0, Math.min(100, score));

  const meetsAllRequirements = hasMinLength && hasUppercase && hasLowercase && hasNumber;

  let level: PasswordStrength['level'];
  let color: string;

  if (!meetsAllRequirements) {
    if (score >= PASSWORD_CONSTANTS.THRESHOLDS.FAIR) {
      level = 'Fair';
      color = 'bg-brand-orange';
    } else {
      level = 'Weak';
      color = 'bg-red-500';
    }
  } else if (score >= PASSWORD_CONSTANTS.THRESHOLDS.STRONG) {
    level = 'Strong';
    color = 'bg-onboarding-success';
  } else {
    level = 'Good';
    color = 'bg-onboarding-success/60';
  }

  return {
    isValid: meetsAllRequirements,
    score,
    level,
    color,
    feedback
  };
};
